import React from 'react';
import kebabCase from 'lodash/kebabCase';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Modal from 'tbk-components/src/components/Modal';

export interface IProfileCalloutBenefit {
	/**
	 * Profile Callout Benefit
	 */
	benefit?: string;
}

export interface ICareersInnerSidebarSuccessMessageProps
	extends IBaseElementProps {
	/**
	 * Success Message Type Selection
	 */
	successMessageType?:
		| 'fullApply-notLoggedIn-accountExists'
		| 'quickApply-notLoggedIn-accountDoesNotExist'
		| 'fullApply-notLoggedIn-accountDoesNotExist'
		| 'quickApply-loggedIn'
		| 'fullApply-loggedIn';
	/**
	 * Success Message Pre Heading
	 */
	successMessagePreHeading?: string;
	/**
	 * Success Message Heading
	 */
	successMessageHeading?: string;
	/**
	 * Success Message Description
	 * @control richtext
	 */
	successMessageDescription?: string;
	/**
	 * Success Message Logo
	 */
	successMessageLogo?: IImage;
	/**
	 * Success Message Business Unit
	 */
	successMessageBusinessUnit?: string;
	/**
	 * Profile Callout Heading
	 */
	profileCalloutHeading?: string;
	/**
	 * Profile Callout Benefits
	 */
	profileCalloutBenefits?: string[];
	/**
	 * Profile Callout Button
	 */
	profileCalloutButton?: ILink;
	/**
	 * Profile Callout Image
	 */
	profileCalloutImage?: IImage;
}

const CareersInnerSidebarSuccessMessage: React.FC<ICareersInnerSidebarSuccessMessageProps> =
	({
		className,
		classNames = [],
		successMessageType,
		successMessagePreHeading,
		successMessageHeading,
		successMessageDescription,
		successMessageLogo,
		successMessageBusinessUnit,
		profileCalloutHeading,
		profileCalloutBenefits,
		profileCalloutButton,
		profileCalloutImage,
	}) => {
		let content = null;

		switch (successMessageType) {
			case 'fullApply-notLoggedIn-accountExists':
				content = (
					<div className="calian-careers-inner-sidebar-success-message__content bg-light-bg mt-1">
						<div className="d-flex flex-column justify-content-center align-items-center gap-5 gap-md-9">
							<div className="calian-careers-inner-sidebar-success-message__content--top">
								<i className="icon icon-success text-primary" />
							</div>
							<div className="calian-careers-inner-sidebar-success-message__content--middle">
								{successMessageHeading && (
									<h2 className="text-center mt-3">
										{successMessageHeading}
									</h2>
								)}
								{successMessageDescription && (
									<p
										className="text-center w-md-75 mb-0 px-md-8 mx-auto fs-18px"
										dangerouslySetInnerHTML={{
											__html: successMessageDescription,
										}}
									/>
								)}
							</div>
							<div className="calian-careers-inner-sidebar-success-message__content--bottom mt-3 mb-8">
								<div className="d-flex flex-row justify-content-center align-items-center">
									{successMessageLogo && (
										<Image
											{...successMessageLogo}
											className="img-fluid me-0 me-md-4"
										/>
									)}
									{successMessageBusinessUnit && (
										<p className="calian-careers-inner-sidebar-success-message__business-unit d-flex flex-row justify-content-center align-items-center mb-0 fs-18px fw-bold">
											{successMessageBusinessUnit}
										</p>
									)}
								</div>
							</div>
						</div>
					</div>
				);
				break;
			case 'quickApply-notLoggedIn-accountDoesNotExist':
				content = (
					<div className="calian-careers-inner-sidebar-success-message__content bg-light-bg">
						<div className="container-fluid">
							<div className="row">
								<div className="calian-careers-inner-sidebar-success-message__content--left col-12 col-md-6 px-md-2 px-lg-8">
									<div className="d-flex flex-column justify-content-center align-items-center gap-5 gap-md-6">
										<div className="calian-careers-inner-sidebar-success-message__content--top">
											<i className="icon icon-success text-primary" />
										</div>
										<div className="calian-careers-inner-sidebar-success-message__content--middle">
											{successMessagePreHeading && (
												<h4 className="text-center text-mid-grey">
													{successMessagePreHeading}
												</h4>
											)}
											{successMessageHeading && (
												<h2 className="text-center mt-3">
													{successMessageHeading}
												</h2>
											)}
											{successMessageDescription && (
												<p
													className="text-center mb-0 mx-auto fs-18px lh-base px-md-2"
													dangerouslySetInnerHTML={{
														__html: successMessageDescription,
													}}
												/>
											)}
										</div>
										<div className="calian-careers-inner-sidebar-success-message__content--bottom mb-10 mb-lg-8">
											<div className="d-flex flex-row justify-content-center align-items-center">
												{successMessageLogo && (
													<Image
														{...successMessageLogo}
														className="img-fluid me-0 me-md-2 me-lg-4"
													/>
												)}
												{successMessageBusinessUnit && (
													<p className="calian-careers-inner-sidebar-success-message__business-unit d-flex flex-row justify-content-center align-items-center mb-0 fs-18px fw-bold">
														{
															successMessageBusinessUnit
														}
													</p>
												)}
											</div>
										</div>
									</div>
								</div>
								<div className="calian-careers-inner-sidebar-success-message__content--right col-12 col-md-6 px-md-2 px-lg-8">
									<div className="d-flex flex-column justify-content-center align-items-center gap-5 gap-md-2 position-relative z-1">
										<div className="calian-careers-inner-sidebar-success-message__content--top">
											{profileCalloutHeading && (
												<h3 className="text-white text-center ps-3 ls-0">
													{profileCalloutHeading}
												</h3>
											)}
										</div>
										<div className="calian-careers-inner-sidebar-success-message__content--middle">
											{profileCalloutBenefits && (
												<ul className="list-unstyled text-white px-6">
													{profileCalloutBenefits.map(
														(benefit, index) => (
															<li
																key={index}
																className="d-flex flex-row justify-content-start align-items-start gap-3 mb-2 pb-1"
															>
																<i className="icon icon-check mt-1" />
																<p className="mb-0 text-white">
																	{benefit}
																</p>
															</li>
														),
													)}
												</ul>
											)}
										</div>
										<div className="calian-careers-inner-sidebar-success-message__content--bottom mb-10 mb-lg-8">
											<div className="d-flex flex-row justify-content-center align-items-center">
												{profileCalloutButton && (
													<Button
														{...profileCalloutButton}
														className="btn btn-dark mt-2"
													/>
												)}
											</div>
										</div>
									</div>
									{profileCalloutImage && (
										<Image
											{...profileCalloutImage}
											className="img-fluid position-absolute bottom-0 end-0 z-0"
										/>
									)}
								</div>
							</div>
						</div>
					</div>
				);
				break;
			case 'fullApply-notLoggedIn-accountDoesNotExist':
				content = (
					<div className="calian-careers-inner-sidebar-success-message__content bg-light-bg">
						<div className="container-fluid">
							<div className="row">
								<div className="calian-careers-inner-sidebar-success-message__content--left col-12 col-md-6 px-md-2 px-lg-8">
									<div className="d-flex flex-column justify-content-center align-items-center gap-5 gap-md-6">
										<div className="calian-careers-inner-sidebar-success-message__content--top">
											<i className="icon icon-success text-primary" />
										</div>
										<div className="calian-careers-inner-sidebar-success-message__content--middle">
											{successMessagePreHeading && (
												<h4 className="text-center text-mid-grey">
													{successMessagePreHeading}
												</h4>
											)}
											{successMessageHeading && (
												<h2 className="text-center mt-3">
													{successMessageHeading}
												</h2>
											)}
											{successMessageDescription && (
												<p
													className="text-center mb-0 mx-auto fs-18px lh-base px-md-2"
													dangerouslySetInnerHTML={{
														__html: successMessageDescription,
													}}
												/>
											)}
										</div>
										<div className="calian-careers-inner-sidebar-success-message__content--bottom mb-10 mb-lg-8">
											<div className="d-flex flex-row justify-content-center align-items-center">
												{successMessageLogo && (
													<Image
														{...successMessageLogo}
														className="img-fluid me-0 me-md-2 me-lg-4"
													/>
												)}
												{successMessageBusinessUnit && (
													<p className="calian-careers-inner-sidebar-success-message__business-unit d-flex flex-row justify-content-center align-items-center mb-0 fs-18px fw-bold">
														{
															successMessageBusinessUnit
														}
													</p>
												)}
											</div>
										</div>
									</div>
								</div>
								<div className="calian-careers-inner-sidebar-success-message__content--right col-12 col-md-6 px-md-2 px-lg-8">
									<div className="d-flex flex-column justify-content-center align-items-center gap-5 gap-md-2 position-relative z-1">
										<div className="calian-careers-inner-sidebar-success-message__content--top">
											{profileCalloutHeading && (
												<h3 className="text-white text-center ps-3 ls-0">
													{profileCalloutHeading}
												</h3>
											)}
										</div>
										<div className="calian-careers-inner-sidebar-success-message__content--middle">
											{profileCalloutBenefits && (
												<ul className="list-unstyled text-white px-6">
													{profileCalloutBenefits.map(
														(benefit, index) => (
															<li
																key={index}
																className="d-flex flex-row justify-content-start align-items-start gap-3 mb-2 pb-1"
															>
																<i className="icon icon-check mt-1" />
																<p className="mb-0 text-white">
																	{benefit}
																</p>
															</li>
														),
													)}
												</ul>
											)}
										</div>
										<div className="calian-careers-inner-sidebar-success-message__content--bottom mb-10 mb-lg-8">
											<div className="d-flex flex-row justify-content-center align-items-center">
												{profileCalloutButton && (
													<Button
														{...profileCalloutButton}
														className="btn btn-dark mt-2"
													/>
												)}
											</div>
										</div>
									</div>
									{profileCalloutImage && (
										<Image
											{...profileCalloutImage}
											className="img-fluid position-absolute bottom-0 end-0 z-0"
										/>
									)}
								</div>
							</div>
						</div>
					</div>
				);
				break;
			case 'quickApply-loggedIn':
				content = (
					<div className="calian-careers-inner-sidebar-success-message__content bg-light-bg">
						<div className="container-fluid">
							<div className="row">
								<div className="calian-careers-inner-sidebar-success-message__content  col-12 w-md-65 mx-auto px-md-2 px-lg-8">
									<div className="d-flex flex-column justify-content-center align-items-center gap-5 gap-md-6">
										<div className="calian-careers-inner-sidebar-success-message__content--top">
											<i className="icon icon-success text-primary" />
										</div>
										<div className="calian-careers-inner-sidebar-success-message__content--middle">
											{successMessagePreHeading && (
												<h4 className="text-center text-mid-grey">
													{successMessagePreHeading}
												</h4>
											)}
											{successMessageHeading && (
												<h2 className="text-center mt-3">
													{successMessageHeading}
												</h2>
											)}
											{successMessageDescription && (
												<p
													className="text-center mb-0 mx-auto fs-18px lh-base px-md-2"
													dangerouslySetInnerHTML={{
														__html: successMessageDescription,
													}}
												/>
											)}
										</div>
										<div className="calian-careers-inner-sidebar-success-message__content--bottom mb-10 mb-lg-8">
											<div className="d-flex flex-row justify-content-center align-items-center">
												{successMessageLogo && (
													<Image
														{...successMessageLogo}
														className="img-fluid me-0 me-md-2 me-lg-4"
													/>
												)}
												{successMessageBusinessUnit && (
													<p className="calian-careers-inner-sidebar-success-message__business-unit d-flex flex-row justify-content-center align-items-center mb-0 fs-18px fw-bold">
														{
															successMessageBusinessUnit
														}
													</p>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				);
				break;
			case 'fullApply-loggedIn':
				content = (
					<div className="calian-careers-inner-sidebar-success-message__content bg-light-bg">
						<div className="container-fluid">
							<div className="row">
								<div className="calian-careers-inner-sidebar-success-message__content col-12 w-md-65 mx-auto px-md-2 px-lg-8">
									<div className="d-flex flex-column justify-content-center align-items-center gap-5 gap-md-6">
										<div className="calian-careers-inner-sidebar-success-message__content--top">
											<i className="icon icon-success text-primary" />
										</div>
										<div className="calian-careers-inner-sidebar-success-message__content--middle">
											{successMessagePreHeading && (
												<h4 className="text-center text-mid-grey">
													{successMessagePreHeading}
												</h4>
											)}
											{successMessageHeading && (
												<h2 className="text-center mt-3">
													{successMessageHeading}
												</h2>
											)}
											{successMessageDescription && (
												<p
													className="text-center mb-0 mx-auto fs-18px lh-base px-md-2"
													dangerouslySetInnerHTML={{
														__html: successMessageDescription,
													}}
												/>
											)}
										</div>
										<div className="calian-careers-inner-sidebar-success-message__content--bottom mb-10 mb-lg-8">
											<div className="d-flex flex-row justify-content-center align-items-center">
												{successMessageLogo && (
													<Image
														{...successMessageLogo}
														className="img-fluid me-0 me-md-2 me-lg-4"
													/>
												)}
												{successMessageBusinessUnit && (
													<p className="calian-careers-inner-sidebar-success-message__business-unit d-flex flex-row justify-content-center align-items-center mb-0 fs-18px fw-bold">
														{
															successMessageBusinessUnit
														}
													</p>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				);
				break;
		}

		return (
			<Modal
				modalClassNames={[
					'calian-careers-inner-sidebar__modal modal-dialog modal-dialog-centered overflow-hidden',
					`${kebabCase(successMessageType)}`,
				]}
				headerClassNames={['modal-header']}
				bodyClassNames={['modal-body']}
			>
				<div className="modal-inner-content p-0">
					<div
						className={
							className ||
							cn(
								'calian-careers-inner-sidebar-success-message',
								...classNames,
							)
						}
					>
						<div className="container-fluid p-0">
							<div className="row g-0">
								<div className="col-12">{content}</div>
							</div>
						</div>
					</div>
				</div>
			</Modal>
		);
	};

export default CareersInnerSidebarSuccessMessage;
